/**
 * CompetitorResultView — Success screen for the Competitor Intelligence module.
 */
import { competitor } from '@/api/client'

interface CompetitorResultViewProps {
  jobId:    string
  meta?:    Record<string, unknown>
  onRerun:  () => void
  onReset:  () => void
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M6.5 2v7M3.5 7l3 3 3-3M2 11h9"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export default function CompetitorResultView({
  jobId, meta, onRerun, onReset,
}: CompetitorResultViewProps) {
  const resultCount = meta?.result_count as number | undefined

  return (
    <div className="animate-fade-up">
      {/* Sticky action bar */}
      <div className="sticky top-14 z-30 glass-bar border-b border-slate-100 px-5 py-3">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto min-w-0">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700
                             rounded-full text-xs font-medium border border-emerald-100 flex-shrink-0">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              Analysis complete
            </span>
            {resultCount !== undefined && (
              <span className="hidden sm:inline text-xs text-slate-400">
                · {resultCount} competitor{resultCount !== 1 ? 's' : ''} analysed
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <a href={competitor.markdownUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> Markdown
            </a>
            <a href={competitor.excelUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> Excel
            </a>
            <button onClick={onReset} className="action-btn-ghost">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M9 2l2 2-7 7H2V9l7-7z"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit
            </button>
            <button
              onClick={onRerun}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-navy-900 text-white
                         rounded-lg text-xs font-medium hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path d="M1.5 5.5A4 4 0 1 1 5.5 9.5M1.5 3v2.5H4"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Re-run
            </button>
          </div>
        </div>
      </div>

      {/* Result card */}
      <div className="max-w-4xl mx-auto px-5 pt-10 pb-16">
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-10 text-center">
          {/* Success icon */}
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-violet-50 flex items-center justify-center text-3xl">
            🔍
          </div>

          <h2 className="text-xl font-bold text-slate-900 mb-2">Analysis ready</h2>
          {resultCount !== undefined && (
            <p className="text-slate-500 text-sm mb-8">
              {resultCount} competitor{resultCount !== 1 ? 's' : ''} fully analysed and compared.
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={competitor.markdownUrl(jobId)}
              download
              className="flex items-center justify-center gap-2 px-7 py-3.5 bg-navy-900 text-white
                         rounded-xl text-sm font-semibold hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <DownloadIcon />
              Download Markdown report
            </a>
            <a
              href={competitor.excelUrl(jobId)}
              download
              className="flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-slate-700
                         border border-slate-200 rounded-xl text-sm font-semibold hover:border-slate-400
                         transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <DownloadIcon />
              Download Excel matrix
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
